import * as THREE from 'three'
import { getLoggerManager } from '@/shared/logging'

const log = getLoggerManager().getLogger('tools')

/** 字体粗细 */
export type FontWeight = 'normal' | 'bold' | 'bolder' | 'lighter' | number
/** 文字相对锚点的摆放位置 */
export type TextSpritePlacement = 'above' | 'below' | 'center'
/** 文字水平对齐方式 */
export type TextSpriteAlign = 'left' | 'center' | 'right'

/** 文字 Sprite 右上角操作区样式。 */
export interface TextSpriteCornerActionOptions {
  /** 操作区显示字符。 */
  glyph: string
  /** 圆形操作区直径，CSS 像素。 */
  size?: number
  /** 与卡片上、右边缘的间距，CSS 像素。 */
  inset?: number
  /** 操作区背景色。 */
  backgroundColor?: string
  /** 操作字符颜色。 */
  textColor?: string
}

/** TextSprite 可用于屏幕命中的 CSS 像素指标。 */
export interface TextSpriteMetrics {
  readonly cssWidth: number
  readonly cssHeight: number
  /** Canvas 纹理经 DPR 还原后的 CSS 像素宽度，包含 2 次幂透明补齐。 */
  readonly textureCssWidth: number
  /** Canvas 纹理经 DPR 还原后的 CSS 像素高度，包含 2 次幂透明补齐。 */
  readonly textureCssHeight: number
  readonly actionBounds?: Readonly<{ x: number; y: number; width: number; height: number }>
}

/** 文字 Sprite 的可选样式参数 */
export interface TextSpriteOptions {
  /** 字体名称，例如 "Arial" */
  fontFace?: string
  /** 字体大小，单位像素 */
  fontSize?: number
  /** 字体粗细 */
  fontWeight?: FontWeight
  /** 文字相对锚点的摆放位置 */
  placement?: TextSpritePlacement
  /** 字体颜色（兼容旧字段，等同 textColor） */
  fontColor?: string
  /** 文字颜色，CSS 颜色字符串 */
  textColor?: string
  /** 背景填充颜色，CSS 颜色字符串 */
  backgroundColor?: string
  /** 内边距，单位像素 */
  padding?: number
  /** 背景圆角半径，单位像素 */
  borderRadius?: number
  /** 边框粗细，单位像素；0 表示无边框 */
  borderThickness?: number
  /** 边框颜色，CSS 颜色字符串 */
  borderColor?: string
  /** 文字描边宽度，单位像素；0 表示不描边 */
  textStrokeWidth?: number
  /** 文字描边颜色，CSS 颜色字符串 */
  textStrokeColor?: string
  /** 文字水平对齐方式 */
  textAlign?: TextSpriteAlign
  /** 可选的右上角操作区，仅负责绘制与暴露命中边界。 */
  topRightAction?: TextSpriteCornerActionOptions
}

type ResolvedCornerActionOptions = Required<TextSpriteCornerActionOptions>

type ResolvedTextSpriteOptions = {
  fontFace: string
  fontSize: number
  fontWeight: FontWeight
  placement: TextSpritePlacement
  fontColor: string
  textColor: string
  backgroundColor: string
  padding: number
  borderRadius: number
  borderThickness: number
  borderColor: string
  textStrokeWidth: number
  textStrokeColor: string
  textAlign: TextSpriteAlign
  topRightAction?: ResolvedCornerActionOptions
}

type TextSpriteUserData = {
  _textCanvas?: HTMLCanvasElement | null
  _textTexture?: THREE.CanvasTexture | null
  _textMaterial?: THREE.SpriteMaterial | null
  _textOptions?: ResolvedTextSpriteOptions | null
  _textBaseScale?: THREE.Vector3 | null
  _textMetrics?: TextSpriteMetrics | null
}

const DEFAULT_OPTIONS: ResolvedTextSpriteOptions = {
  fontFace: 'Arial',
  fontSize: 64,
  fontWeight: 'bold',
  placement: 'center',
  fontColor: '#ffffff',
  textColor: '#ffffff',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  padding: 8,
  borderRadius: 4,
  borderThickness: 0,
  borderColor: 'rgba(0, 0, 0, 0)',
  textStrokeWidth: 0,
  textStrokeColor: 'rgba(0, 0, 0, 1)',
  textAlign: 'center',
}

const DEFAULT_CORNER_ACTION: Omit<ResolvedCornerActionOptions, 'glyph'> = {
  size: 16,
  inset: 3,
  backgroundColor: '#d41620',
  textColor: '#ffffff',
}

function getSpriteState(sprite: THREE.Sprite): TextSpriteUserData {
  return sprite.userData as TextSpriteUserData
}

function resolveTextSpriteOptions(options?: TextSpriteOptions): ResolvedTextSpriteOptions {
  const fontColor = options?.fontColor ?? DEFAULT_OPTIONS.fontColor
  const action = options?.topRightAction

  return {
    fontFace: options?.fontFace ?? DEFAULT_OPTIONS.fontFace,
    fontSize: options?.fontSize ?? DEFAULT_OPTIONS.fontSize,
    fontWeight: options?.fontWeight ?? DEFAULT_OPTIONS.fontWeight,
    placement: options?.placement ?? DEFAULT_OPTIONS.placement,
    fontColor,
    textColor: options?.textColor ?? fontColor,
    backgroundColor: options?.backgroundColor ?? DEFAULT_OPTIONS.backgroundColor,
    padding: options?.padding ?? DEFAULT_OPTIONS.padding,
    borderRadius: options?.borderRadius ?? DEFAULT_OPTIONS.borderRadius,
    borderThickness: options?.borderThickness ?? DEFAULT_OPTIONS.borderThickness,
    borderColor: options?.borderColor ?? DEFAULT_OPTIONS.borderColor,
    textStrokeWidth: options?.textStrokeWidth ?? DEFAULT_OPTIONS.textStrokeWidth,
    textStrokeColor: options?.textStrokeColor ?? DEFAULT_OPTIONS.textStrokeColor,
    textAlign: options?.textAlign ?? DEFAULT_OPTIONS.textAlign,
    ...(action && {
      topRightAction: {
        glyph: action.glyph,
        size: action.size ?? DEFAULT_CORNER_ACTION.size,
        inset: action.inset ?? DEFAULT_CORNER_ACTION.inset,
        backgroundColor: action.backgroundColor ?? DEFAULT_CORNER_ACTION.backgroundColor,
        textColor: action.textColor ?? DEFAULT_CORNER_ACTION.textColor,
      },
    }),
  }
}

function nextPowerOfTwo(v: number): number {
  let n = Math.ceil(v)
  n--
  n |= n >> 1
  n |= n >> 2
  n |= n >> 4
  n |= n >> 8
  n |= n >> 16
  return n + 1
}

function drawRoundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): void {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + w - r, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + r)
  ctx.lineTo(x + w, y + h - r)
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
  ctx.lineTo(x + r, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - r)
  ctx.lineTo(x, y + r)
  ctx.quadraticCurveTo(x, y, x + r, y)
  ctx.closePath()
}

function renderTextToCanvas(
  canvas: HTMLCanvasElement,
  text: string,
  opts: ResolvedTextSpriteOptions
): TextSpriteMetrics {
  const ctx = canvas.getContext('2d')!
  const {
    fontFace,
    fontSize,
    fontWeight,
    textColor,
    backgroundColor,
    padding,
    borderRadius,
    borderThickness,
    borderColor,
    textStrokeWidth,
    textStrokeColor,
    textAlign,
    topRightAction,
  } = opts

  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio : 1
  const font = `${fontWeight} ${fontSize}px ${fontFace}`
  const lines = String(text).split(/\r?\n/)

  ctx.font = font
  const lineMetrics = lines.map((line) => ctx.measureText(line))
  const textWidth = lineMetrics.reduce((max, item) => Math.max(max, item.width), 0)

  let lineHeight = fontSize * 1.3
  for (const metrics of lineMetrics) {
    const ascent = metrics.actualBoundingBoxAscent ?? fontSize * 0.8
    const descent = metrics.actualBoundingBoxDescent ?? fontSize * 0.2
    lineHeight = Math.max(lineHeight, ascent + descent)
  }

  const strokePadding = Math.max(0, Math.ceil(textStrokeWidth))
  const innerPadding = padding + strokePadding

  const actionReservedWidth = topRightAction ? topRightAction.size + topRightAction.inset : 0
  const cssWidth = textWidth + innerPadding * 2 + borderThickness * 2 + actionReservedWidth
  const cssHeight = lineHeight * lines.length + innerPadding * 2 + borderThickness * 2

  canvas.width = nextPowerOfTwo(Math.max(1, cssWidth * dpr))
  canvas.height = nextPowerOfTwo(Math.max(1, cssHeight * dpr))
  const textureCssWidth = canvas.width / dpr
  const textureCssHeight = canvas.height / dpr

  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.font = font
  ctx.textBaseline = 'middle'
  ctx.textAlign = textAlign
  ctx.lineJoin = 'round'

  ctx.fillStyle = backgroundColor
  ctx.strokeStyle = borderColor
  ctx.lineWidth = borderThickness

  const rectX = borderThickness > 0 ? borderThickness / 2 : 0
  const rectY = borderThickness > 0 ? borderThickness / 2 : 0
  const rectWidth = Math.max(cssWidth - borderThickness, 0)
  const rectHeight = Math.max(cssHeight - borderThickness, 0)
  const radius = Math.min(borderRadius, rectWidth / 2, rectHeight / 2)

  drawRoundRect(ctx, rectX, rectY, rectWidth, rectHeight, radius)
  ctx.fill()
  if (borderThickness > 0) {
    ctx.stroke()
  }

  const textLeft = borderThickness + innerPadding
  const textRight = cssWidth - borderThickness - innerPadding - actionReservedWidth
  const textCenter = (textLeft + textRight) / 2

  ctx.fillStyle = textColor
  ctx.strokeStyle = textStrokeColor
  ctx.lineWidth = textStrokeWidth

  lines.forEach((line, index) => {
    const x = textAlign === 'left' ? textLeft : textAlign === 'right' ? textRight : textCenter
    const y = borderThickness + innerPadding + lineHeight * (index + 0.5)
    if (textStrokeWidth > 0) {
      ctx.strokeText(line, x, y)
    }
    ctx.fillText(line, x, y)
  })

  if (!topRightAction) {
    return { cssWidth, cssHeight, textureCssWidth, textureCssHeight }
  }

  const actionBounds = {
    x: cssWidth - borderThickness - topRightAction.inset - topRightAction.size,
    y: borderThickness + topRightAction.inset,
    width: topRightAction.size,
    height: topRightAction.size,
  }
  const actionCenterX = actionBounds.x + actionBounds.width / 2
  const actionCenterY = actionBounds.y + actionBounds.height / 2
  ctx.beginPath()
  ctx.arc(actionCenterX, actionCenterY, topRightAction.size / 2, 0, Math.PI * 2)
  ctx.fillStyle = topRightAction.backgroundColor
  ctx.fill()
  ctx.font = `bold ${Math.max(10, topRightAction.size * 0.9)}px ${fontFace}`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillStyle = topRightAction.textColor
  ctx.fillText(topRightAction.glyph, actionCenterX, actionCenterY)

  return { cssWidth, cssHeight, textureCssWidth, textureCssHeight, actionBounds }
}

function applyBaseScale(sprite: THREE.Sprite, cssWidth: number, cssHeight: number): void {
  const scale = 0.01
  const baseScale = new THREE.Vector3(cssWidth * scale, cssHeight * scale, 1.0)
  const state = getSpriteState(sprite)
  state._textBaseScale = baseScale
  sprite.scale.copy(baseScale)
}

function applyPlacement(sprite: THREE.Sprite, placement: TextSpritePlacement): void {
  const centerY = placement === 'above' ? 0 : placement === 'below' ? 1 : 0.5
  sprite.center.set(0.5, centerY)
}

/**
 * 创建带文字与背景的 Sprite。
 *
 * @param text - 显示文字
 * @param options - 样式选项，未提供字段使用默认值
 * @returns 挂载有 CanvasTexture 材质的 THREE.Sprite
 */
export function createTextSprite(text: string, options?: TextSpriteOptions): THREE.Sprite {
  const opts = resolveTextSpriteOptions(options)

  const canvas = document.createElement('canvas')
  const metrics = renderTextToCanvas(canvas, text, opts)

  const texture = new THREE.CanvasTexture(canvas)
  texture.minFilter = THREE.LinearFilter
  texture.magFilter = THREE.LinearFilter
  texture.generateMipmaps = false
  texture.needsUpdate = true

  const material = new THREE.SpriteMaterial({
    map: texture,
    depthTest: false,
    depthWrite: false,
    transparent: true,
  })

  const sprite = new THREE.Sprite(material)
  applyPlacement(sprite, opts.placement)
  applyBaseScale(sprite, metrics.cssWidth, metrics.cssHeight)

  const state = getSpriteState(sprite)
  state._textCanvas = canvas
  state._textTexture = texture
  state._textMaterial = material
  state._textOptions = opts
  state._textMetrics = metrics

  return sprite
}

/**
 * 更新已有文字 Sprite 的显示文本。
 *
 * @param sprite - 由 createTextSprite 创建的 Sprite
 * @param text - 新文字内容
 */
export function updateTextSprite(sprite: THREE.Sprite, text: string): void {
  const state = getSpriteState(sprite)
  const canvas = state._textCanvas ?? undefined
  const texture = state._textTexture ?? undefined
  const opts = state._textOptions ?? undefined

  if (!canvas || !texture || !opts) {
    log.warn('updateTextSprite: missing sprite text state, skip update')
    return
  }

  const metrics = renderTextToCanvas(canvas, text, opts)
  texture.needsUpdate = true
  applyBaseScale(sprite, metrics.cssWidth, metrics.cssHeight)
  state._textMetrics = metrics
}

/** 获取文字 Sprite 当前的 CSS 像素指标。 */
export function getTextSpriteMetrics(sprite: THREE.Sprite): TextSpriteMetrics | null {
  const metrics = getSpriteState(sprite)._textMetrics
  if (!metrics) {
    return null
  }
  return {
    cssWidth: metrics.cssWidth,
    cssHeight: metrics.cssHeight,
    textureCssWidth: metrics.textureCssWidth,
    textureCssHeight: metrics.textureCssHeight,
    ...(metrics.actionBounds && { actionBounds: { ...metrics.actionBounds } }),
  }
}

/**
 * 根据相机距离与视口高度更新文字 Sprite 的世界缩放，使其显示为固定像素高度。
 *
 * @param sprite - 由 createTextSprite 创建的 Sprite
 * @param camera - 当前相机，用于计算每像素对应的世界距离
 * @param viewportHeight - 视口高度，单位像素；≤0 时直接返回
 * @param targetPx - 目标屏幕高度，单位像素，默认 70
 */
export function updateTextSpriteScaleForCamera(
  sprite: THREE.Sprite,
  camera: THREE.Camera,
  viewportHeight: number,
  targetPx = 70
): void {
  const state = getSpriteState(sprite)
  const baseScale = state._textBaseScale

  if (!baseScale || viewportHeight <= 0) {
    return
  }

  let worldPerPixel = 1 / viewportHeight

  if (camera instanceof THREE.PerspectiveCamera) {
    const distance = camera.position.distanceTo(sprite.getWorldPosition(new THREE.Vector3()))
    const fovRad = THREE.MathUtils.degToRad(camera.fov)
    worldPerPixel = (2 * distance * Math.tan(fovRad / 2)) / viewportHeight
  } else if (camera instanceof THREE.OrthographicCamera) {
    const frustumHeight = (camera.top - camera.bottom) / Math.max(camera.zoom, 1e-6)
    worldPerPixel = frustumHeight / viewportHeight
  }

  const scaleFactor = targetPx * worldPerPixel
  sprite.scale.copy(baseScale).multiplyScalar(scaleFactor)
}

/**
 * 释放文字 Sprite 关联的纹理、材质与 canvas 引用。
 *
 * @param sprite - 由 createTextSprite 创建的 Sprite
 */
export function disposeTextSprite(sprite: THREE.Sprite): void {
  const state = getSpriteState(sprite)

  state._textTexture?.dispose()
  state._textMaterial?.dispose()

  state._textCanvas = null
  state._textTexture = null
  state._textMaterial = null
  state._textOptions = null
  state._textBaseScale = null
  state._textMetrics = null
}
